import { Hidden, makeStyles, Theme, Tooltip } from "@material-ui/core";
import React, { useMemo } from "react";
import { Filter, ViClaimDamagedPart } from "../models";
import { capitalize, groupBy, sortByWorkAction, Translator } from "../services";
import { Chart } from "react-google-charts";
import { Column } from "react-table";
import FooterTable from "./FooterTable";

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "100%",
      overflow: "scroll",
    },
  },
  chart: {
    chart: {
      paddingLeft: `${theme.goldenRatio(-1)}rem`,
      width: "300px",
      height: "30vh",
      "& svg": {
        overflow: "visible !important",
      },
      "& svg g:last-of-type": {
        pointerEvents: "none",
      },
    },
  },
}));

function OperationsFooter({
  claimDamagedParts,
  hoverFilter,
  isFilterMatch,
  setHoverFilter,
  className,
}: {
  claimDamagedParts?: ViClaimDamagedPart[];
  hoverFilter: Filter[];
  isFilterMatch: (filterProps: Filter[]) => boolean | undefined;
  setHoverFilter?: (filters: Filter[]) => void;
  className?: string;
}) {
  const classes = useStyles();

  const tableData = useMemo(
    () =>
      claimDamagedParts?.map((cDP) => {
        const partGroupName = capitalize(cDP.partGroupName);
        const partGroupP = cDP.p;
        const partGroupSide = capitalize(cDP.sideResult?.side);
        const partGroupSideP = cDP.sideResult?.p;
        const filters: Filter[] = [];
        const level0 = {
          partGroupName,
          partGroupP,
          partGroupSide,
          partGroupSideP,
          filters,
        };
        return {
          ...level0,
          subRows: cDP.physicalParts.map((pP) => {
            const physicalPartName = capitalize(pP.genericPartName);
            const physicalPartP = pP.p;
            const physicalPartSGN = pP.standardGuideNumber;
            const pPFilters = pP.objectDetectionLabels?.map((label) => ({
              genericPartName: label,
              side: cDP.sideResult ? cDP.sideResult?.side : "NONE",
            }));
            filters.push(...(Array.isArray(pPFilters) ? pPFilters : []));
            const physicalPartPosition = pP.position.code;
            const physicalPartPositionP = pP.position.p;
            const level1 = {
              physicalPartName,
              physicalPartP,
              physicalPartSGN,
              physicalPartPosition,
              physicalPartPositionP,
              workActions: pP.workActionResults.map((wA) => {
                const workActionName = capitalize(wA.workAction);
                const workActionP = wA.p;
                const workActionIsPrimary = wA.isPrimary;
                return {
                  workActionName,
                  workActionP,
                  workActionIsPrimary,
                };
              }),
              filters: pPFilters,
            };
            return {
              ...level0,
              ...level1,
              subRows: pP.workActionResults
                .flatMap((wA) => wA.operations)
                .map((operation) => {
                  const operationId = operation.operationId;
                  const operationP = operation.p;
                  const level2 = {
                    operationId,
                    operationP,
                    filters: pPFilters,
                  };
                  return {
                    ...level0,
                    ...level1,
                    ...level2,
                  };
                }),
            };
          }),
        };
      }),
    [claimDamagedParts]
  );

  const columns: Column[] = [
    {
      id: "part",
      accessor: ({ partGroupName, physicalPartName }: any) =>
        physicalPartName || partGroupName,
      Header: "Part",
      Cell: ({ cell, row: { depth, original } }: any) => {
        if (!cell.value || cell.value === "NONE" || depth > 1) return "";
        if (original.physicalPartSGN) cell.value += ` (${original.physicalPartSGN})`;
        const p = depth === 0 ? original.partGroupP : original.physicalPartP;
        return (
          <>
            {cell.value}&nbsp;
            <small>{`${Math.max(Math.round(p * 100), 1)}%`}</small>
          </>
        );
      },
    },
    {
      id: "position",
      accessor: ({ partGroupSide, physicalPartPosition }: any) =>
        physicalPartPosition || partGroupSide,
      Header: "Position",
      Cell: ({ cell, row: { depth, original } }: any) => {
        if (!cell.value || cell.value === "NONE" || depth > 1 || depth < 0) return "";
        const value =
          depth === 0
            ? cell.value
            : Translator.translate(`positions.${cell.value}`, false);
        return (
          <>
            {value}&nbsp;
            <small>{`${Math.max(
              Math.round(
                (original.physicalPartPositionP || original.partGroupSideP) * 100
              ),
              1
            )}%`}</small>
          </>
        );
      },
    },
    {
      id: "operation",
      accessor: "workActionName",
      Header: "Operations",
      Cell: ({ cell, row: { depth, original, isExpanded } }: any) => {
        if (cell.value === "NONE" || (depth === 1 && isExpanded)) return "";
        if (depth === 0) {
          const primaryActions = original.subRows
            .map((subRow: any) =>
              subRow.workActions.find(({ workActionIsPrimary: is }: any) => is)
            )
            .filter(Boolean);
          const groupedPrimaryActions = groupBy(primaryActions, ["workActionName"]);
          return Object.entries(groupedPrimaryActions)
            .sort(([, a]: any, [, b]: any) =>
              sortByWorkAction(a[0], b[0], "workActionName")
            )
            .map(([name, workActions], i, arr) => (
              <Tooltip title="Primary Operations for Part Group" enterNextDelay={150}>
                <span>
                  {name}&nbsp;
                  <small>{i < arr.length - 1 ? " | " : ""}</small>
                </span>
              </Tooltip>
            ));
        }
        if (depth === 1) {
          const primary = original.workActions.find(
            ({ workActionIsPrimary: is }: any) => is
          );
          return primary ? (
            <>
              <Tooltip title="Primary Operation for Physical Part" enterNextDelay={150}>
                <span>{primary.workActionName}&nbsp;</span>
              </Tooltip>
              <Tooltip title="Confidence for Primary Operation" enterNextDelay={150}>
                <small>{`${Math.max(Math.round(primary.workActionP * 100), 1)}%`}</small>
              </Tooltip>
            </>
          ) : (
            ""
          );
        }
        if (depth === 2) {
          const operationLabel = Translator.translate(
            `operations.${original.operationId}`,
            false
          );
          return (
            <span style={{ whiteSpace: "nowrap", fontStyle: "italics" }}>
              {operationLabel ? operationLabel : original.operationId}
              {operationLabel ? <small>&nbsp;{original.operationId}</small> : null}
            </span>
          );
        }
        return "";
      },
    },
    {
      id: "confidence",
      accessor: ({ partGroupP, physicalPartP, workActionP, operationP }: any) =>
        operationP || workActionP || physicalPartP || partGroupP,
      Header: "Confidence",
      Cell: ({ cell }: any) => `${Math.max(Math.round(cell.value * 100), 1)}%`,
      sortType: (a: any, b: any) => a.values.confidence - b.values.confidence,
    },
  ];

  const chartData: [string, number][] = Object.entries(
    tableData
      ?.map((el) =>
        el.subRows.flatMap((el) =>
          el.workActions
            .filter((el) => el.workActionIsPrimary)
            .map((el) => el.workActionName)
        )
      )
      .flat(Infinity)
      .reduce(
        (obj: any, el: any) => ({
          ...obj,
          [el]: (obj[el] || 0) + 1,
        }),
        {} as any
      ) || []
  );

  return (
    <div className={[className, classes.wrapper].join(" ")}>
      <FooterTable
        {...{
          data: tableData,
          columns,
          hoverFilter,
          setHoverFilter,
          isFilterMatch,
          expander: true,
          initialState: { sortBy: [{ id: "confidence", desc: true }] },
        }}
      />
      {Boolean(chartData.length) && (
        <Hidden mdDown>
          <Chart
            className={classes.chart}
            style={{ alignSelf: "center", margin: "auto" }}
            chartType="PieChart"
            data={[["Operation", "Count"], ...chartData]}
            options={{
              backgroundColor: "none",
              is3D: true,
              chartArea: { height: "100%", width: "100%" },
              legend: { position: "right", alignment: "center" },
            }}
          />
        </Hidden>
      )}
    </div>
  );
}

export default React.memo(OperationsFooter);
